import type { RouterOutputs } from "@gately/api";
import type { FormVisitorType } from "@gately/validators";
import { useMemo, useState } from "react";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { PanelRightClose, PanelRightOpen } from "lucide-react";

import { cn } from "@gately/ui";
import { Button } from "@gately/ui/button";
import { ConfirmDialog } from "@gately/ui/confirm-dialog";
import { Input } from "@gately/ui/input";
import { Label } from "@gately/ui/label";
import { SettingsSection } from "@gately/ui/settings";
import { Switch } from "@gately/ui/switch";
import { Textarea } from "@gately/ui/textarea";
import { toast } from "@gately/ui/toast";
import { formVisitorTypeLabels, formVisitorTypes } from "@gately/validators";

import type { BuilderField } from "~/lib/form-builder";
import { AddFieldButton, FieldEditor } from "~/components/forms/FieldEditor";
import { FormRenderer } from "~/components/forms/FormRenderer";
import {
  addChild,
  createField,
  firstSchemaError,
  moveField,
  newFieldId,
  removeField,
  toFormSchema,
  toPreviewSchema,
  updateField,
} from "~/lib/form-builder";
import { orpc } from "~/lib/orpc";
import { frenchErrorMessage } from "~/lib/orpc-error";
import { useMe } from "~/lib/use-me";
import { activeOrgType, useTerms } from "~/lib/use-terms";

export const Route = createFileRoute("/_dashboard/reglages/formulaires")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(orpc.form.list.queryOptions()),
  component: FormsPage,
});

type FormRow = RouterOutputs["form"]["list"][number];

function toBuilderFields(schema: FormRow["schema"]): BuilderField[] {
  return schema.fields.map((f) => ({
    ...f,
    id: newFieldId(),
    fields:
      f.type === "group"
        ? f.fields.map((c) => ({ ...c, id: newFieldId() }))
        : undefined,
  }));
}

function FormsPage() {
  const queryClient = useQueryClient();
  const me = useMe();
  const { data: forms } = useSuspenseQuery(orpc.form.list.queryOptions());
  const [editing, setEditing] = useState<FormVisitorType | null>(null);
  const [disabling, setDisabling] = useState<{
    id: string;
    visitorType: FormVisitorType;
  } | null>(null);
  const isOffice = activeOrgType(me) === "office";

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.form.list.key() });

  const archive = useMutation(
    orpc.form.archive.mutationOptions({
      onSuccess: async () => {
        setDisabling(null);
        toast.success("Formulaire désactivé.");
        await invalidate();
        // Every invite surface caches the active form per type.
        await queryClient.invalidateQueries({
          queryKey: orpc.invite.activeForm.key(),
        });
      },
      onError: (err) => {
        setDisabling(null);
        toast.error(frenchErrorMessage(err, "Échec de la désactivation."));
        void invalidate();
      },
    }),
  );

  if (editing) {
    return (
      <FormBuilder
        visitorType={editing}
        forms={forms}
        onClose={() => setEditing(null)}
        onSaved={invalidate}
      />
    );
  }

  return (
    <SettingsSection
      title="Formulaires"
      hint={
        <>
          Construisez les formulaires exigés à la création d'une invitation{" "}
          {isOffice ? "visiteur ou intervenant" : "visiteur, locataire ou intervenant"}.
        </>
      }
      surface={false}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Every vertical builds visiteur and intervenant forms; locataire is
            résidence-only (visitorTypesByOrgType). */}
        {formVisitorTypes
          .filter((vt) => !isOffice || vt !== "locataire")
          .map((vt) => {
            const active = forms.find(
              (f) => f.visitorType === vt && f.status === "active",
            );
            const draft = forms.find(
              (f) => f.visitorType === vt && f.status === "draft",
            );
            const archived = forms.some(
              (f) => f.visitorType === vt && f.status === "archived",
            );
            return (
              <div
                key={vt}
                className="flex flex-col gap-2 rounded-lg border border-neutral-200 bg-white p-4"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {formVisitorTypeLabels[vt]}
                  </span>
                  <span className="ml-auto text-xs text-neutral-500">
                    {active
                      ? `Actif · v${active.version}`
                      : draft
                        ? "Brouillon"
                        : archived
                          ? "Désactivé"
                          : "Aucun"}
                  </span>
                </div>
                <Button variant="outline" onClick={() => setEditing(vt)}>
                  {active || draft
                    ? "Modifier"
                    : archived
                      ? "Réactiver"
                      : "Créer"}
                </Button>
                {active ? (
                  <Button
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() =>
                      setDisabling({ id: active.id, visitorType: vt })
                    }
                  >
                    Désactiver
                  </Button>
                ) : null}
              </div>
            );
          })}
      </div>
      <ConfirmDialog
        open={disabling !== null}
        onOpenChange={(o) => {
          if (!o) setDisabling(null);
        }}
        title="Désactiver ce formulaire ?"
        description={
          disabling ? (
            <>
              Les invitations{" "}
              <strong>
                {formVisitorTypeLabels[disabling.visitorType].toLowerCase()}
              </strong>{" "}
              n'exigeront plus de formulaire : les visiteurs qui ne l'ont pas
              encore rempli n'auront plus à le faire. Les réponses déjà reçues
              et les demandes en attente de validation sont conservées. Vous
              pourrez le réactiver en le publiant à nouveau.
            </>
          ) : null
        }
        confirmLabel="Désactiver"
        pendingLabel="Désactivation…"
        destructive
        pending={archive.isPending}
        onConfirm={() => {
          if (disabling) archive.mutate({ id: disabling.id });
        }}
      />
    </SettingsSection>
  );
}

function FormBuilder({
  visitorType,
  forms,
  onClose,
  onSaved,
}: {
  visitorType: FormVisitorType;
  forms: FormRow[];
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const terms = useTerms();
  const existing = useMemo(() => {
    const draft = forms.find(
      (f) => f.visitorType === visitorType && f.status === "draft",
    );
    const active = forms.find(
      (f) => f.visitorType === visitorType && f.status === "active",
    );
    // After « Désactiver », start from the last published version (the list
    // is newest-first per type) so re-enabling is one « Publier » away.
    const archived = forms.find(
      (f) => f.visitorType === visitorType && f.status === "archived",
    );
    return draft ?? active ?? archived ?? null;
  }, [forms, visitorType]);

  // If we edit an active (non-draft) form, upsert with no id -> creates a new draft.
  const editableId = existing?.status === "draft" ? existing.id : undefined;

  const [title, setTitle] = useState(
    existing?.title ?? formVisitorTypeLabels[visitorType],
  );
  const [description, setDescription] = useState(existing?.description ?? "");
  const [requiresApproval, setRequiresApproval] = useState(
    existing?.requiresApproval ?? false,
  );
  const [fields, setFields] = useState<BuilderField[]>(
    existing ? toBuilderFields(existing.schema) : [],
  );
  const [preview, setPreview] = useState<Record<string, unknown>>({});
  const [previewOpen, setPreviewOpen] = useState(true);

  const upsert = useMutation(
    orpc.form.upsert.mutationOptions({
      // A refused schema (e.g. a legacy consent field) says why in French
      // through its refine's issue, not oRPC's « Input validation failed ».
      onError: (err) =>
        toast.error(frenchErrorMessage(err, "Échec de l'enregistrement.")),
    }),
  );
  const publish = useMutation(
    orpc.form.publish.mutationOptions({
      onSuccess: async () => {
        toast.success("Formulaire publié.");
        await onSaved();
        onClose();
      },
      onError: (err) =>
        toast.error(frenchErrorMessage(err, "Échec de la publication.")),
    }),
  );

  function buildPayload() {
    const schema = toFormSchema(fields); // throws if invalid
    return {
      id: editableId,
      visitorType,
      title: title.trim(),
      description: description.trim() || undefined,
      schema,
      requiresApproval,
    };
  }

  function saveDraft() {
    const err = firstSchemaError(fields);
    if (err) {
      toast.error(`Formulaire incomplet : ${err}`);
      return;
    }
    const payload = buildPayload();
    upsert.mutate(payload, {
      onSuccess: () => {
        void (async () => {
          toast.success("Brouillon enregistré.");
          await onSaved();
          onClose();
        })();
      },
    });
  }

  async function saveAndPublish() {
    const err = firstSchemaError(fields);
    if (err) {
      toast.error(`Formulaire incomplet : ${err}`);
      return;
    }
    const payload = buildPayload();
    try {
      const saved = await upsert.mutateAsync(payload);
      if (!saved) return;
      publish.mutate({ id: saved.id });
    } catch {
      // error toast already shown by the upsert mutation's onError
    }
  }

  // Live preview: renders every field that is ready (has a key + label);
  // incomplete fields are skipped rather than blanking the whole preview.
  const previewSchema = useMemo(() => toPreviewSchema(fields), [fields]);

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <Button variant="ghost" onClick={onClose}>
          ← Retour
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPreviewOpen((v) => !v)}
          aria-expanded={previewOpen}
          aria-controls="form-builder-preview"
        >
          {previewOpen ? (
            <>
              <PanelRightClose />
              Masquer l'aperçu
            </>
          ) : (
            <>
              <PanelRightOpen />
              Afficher l'aperçu
            </>
          )}
        </Button>
        <span className="ml-auto text-xs text-neutral-500">
          {formVisitorTypeLabels[visitorType]}
        </span>
      </div>

      <div className={cn("grid gap-6", previewOpen && "lg:grid-cols-2")}>
        <div className="flex flex-col gap-4 rounded-lg border border-neutral-200 bg-white p-5">
          <div className="flex flex-col gap-1">
            <Label>Titre</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Description</Label>
            <Textarea
              className="min-h-16"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-3 text-sm">
            <Switch
              checked={requiresApproval}
              onCheckedChange={setRequiresApproval}
            />
            Nécessite l'approbation {terms.ofTheAdmin} avant activation de
            l'invitation
          </label>

          <div className="flex flex-col gap-3">
            {fields.map((field) => (
              <FieldEditor
                key={field.id}
                field={field}
                onMove={(dir) =>
                  setFields((fs) => moveField(fs, field.id, dir))
                }
                onRemove={() => setFields((fs) => removeField(fs, field.id))}
                onPatch={(patch) =>
                  setFields((fs) => updateField(fs, field.id, patch))
                }
                onAddChild={(child) =>
                  setFields((fs) => addChild(fs, field.id, child))
                }
                onPatchChild={(childId, patch) =>
                  setFields((fs) =>
                    fs.map((f) =>
                      f.id === field.id
                        ? {
                            ...f,
                            fields: updateField(f.fields ?? [], childId, patch),
                          }
                        : f,
                    ),
                  )
                }
                onRemoveChild={(childId) =>
                  setFields((fs) =>
                    fs.map((f) =>
                      f.id === field.id
                        ? { ...f, fields: removeField(f.fields ?? [], childId) }
                        : f,
                    ),
                  )
                }
              />
            ))}
          </div>

          <AddFieldButton
            label="Ajouter un champ"
            onAdd={(type) =>
              setFields((fs) => [...fs, createField(type, newFieldId())])
            }
          />

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={saveDraft}
              disabled={upsert.isPending}
            >
              Enregistrer le brouillon
            </Button>
            <Button
              onClick={saveAndPublish}
              disabled={upsert.isPending || publish.isPending}
            >
              Publier
            </Button>
          </div>
        </div>

        {previewOpen ? (
          <div
            id="form-builder-preview"
            className="rounded-lg border border-neutral-200 bg-white p-4 lg:sticky lg:top-20 lg:self-start"
          >
            <p className="mb-3 text-xs font-medium text-neutral-500 uppercase">
              Aperçu
            </p>
            {previewSchema.fields.length > 0 ? (
              <FormRenderer
                schema={previewSchema}
                value={preview}
                onChange={setPreview}
                disabled
              />
            ) : (
              <p className="text-sm text-neutral-500">
                Ajoutez des champs (avec libellé et clé) pour voir l'aperçu.
              </p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
